/**
 * 計算過程と詳細レポートの本文（アコーディオン・モーダル共通）。
 * 漸進的開示で「ホワイトボックス」を表示し、手計算で検証可能にする。
 */

import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CalculationStepsView from '../CalculationStepsView';
import AnovaCalculationDetails from './AnovaCalculationDetails';
import DoseResponseCalculationDetails from './DoseResponseCalculationDetails';
import type { LogRankTestResult } from '../../../../utils/statistics/survival';
import type {
  AnalysisOptions,
  DataRow,
  GroupComparisonRow,
} from '../../../../stores/systems/keisuke/dashboardStore';
import {
  formatKeisukeNumber,
  formatMethodStatValue,
  formatStatKeyLabel,
  formatStatKeyPairLabel,
} from './keisukeDashboardStatLabels';
import type { ReactNode } from 'react';

export interface CalculationDetailsContentProps {
  analysisType: string;
  rawResult: unknown;
  /** アコーディオン内表示用にコンパクトにする（見出しレベルなど） */
  compact?: boolean;
  analysisOptions?: AnalysisOptions;
  gridData?: DataRow[];
  anovaPairwise?: GroupComparisonRow[];
  ttestComparisonCount?: number;
}

const formatNumber = formatKeisukeNumber;

function MonoBox({ children }: { children: ReactNode }) {
  return <div className="bg-light p-3 rounded small font-monospace">{children}</div>;
}

export default function CalculationDetailsContent({
  analysisType,
  rawResult,
  compact = false,
  analysisOptions,
  gridData = [],
  anovaPairwise,
  ttestComparisonCount = 1,
}: CalculationDetailsContentProps) {
  const { t } = useTranslation();

  if (!rawResult || typeof rawResult !== 'object') {
    return (
      <p className="text-muted mb-0">
        {t('keisuke.dashboard.calculationDetailsNotAvailable')}
      </p>
    );
  }

  const r = rawResult as Record<string, unknown>;

  if (analysisType === 'survival' && r.logRankTest) {
    return (
      <CalculationStepsView logRankTest={r.logRankTest as LogRankTestResult} />
    );
  }

  if (analysisType === 't-test' && r.result) {
    const result = r.result as Record<string, unknown>;
    const groupNames = r.groupNames as [string, string] | undefined;
    const groups = r.groups as Record<string, number[]> | undefined;
    const before = r.before as number[] | undefined;
    const after = r.after as number[] | undefined;
    const testType: 'unpaired' | 'paired' = before && after ? 'paired' : 'unpaired';
    const mean1 = Number(result.mean1);
    const mean2 = Number(result.mean2);
    const stdDev1 = Number(result.stdDev1);
    const stdDev2 = Number(result.stdDev2);
    const n1 = Number(result.n1);
    const n2 = Number(result.n2);
    const tVal = Number(result.t);
    const df = Number(result.df);
    const pValue = Number(result.pValue);
    const method = String(result.method ?? 'welch');
    const namedGroups =
      groupNames && groupNames.length >= 2
        ? { a: groupNames[0], b: groupNames[1] }
        : testType === 'paired'
          ? {
              a: t('keisuke.dashboard.inputForm.defaultBefore'),
              b: t('keisuke.dashboard.inputForm.defaultAfter'),
            }
          : undefined;
    const ci = result.confidenceInterval as { lower: number; upper: number } | undefined;

    if (method === 'mann-whitney' || method === 'wilcoxon') {
      const Title = compact ? 'h6' : 'h5';
      const label =
        method === 'mann-whitney'
          ? t('keisuke.dashboard.ttest.mannWhitney')
          : t('keisuke.dashboard.ttest.wilcoxon');
      return (
        <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
          <Title className="mb-2">
            {t('keisuke.ttest.calculationProcess')}
          </Title>
          <p className="mb-2">{label}</p>
          <pre className="bg-light p-3 rounded mb-3 small">
            {method === 'mann-whitney'
              ? `1. 2群の値を結合し、平均順位（タイは中間順位）を付与\n2. U = min(U₁, U₂)、正規近似 z = (U − μ_U) / σ_U\n3. 両側 p 値（標準正規分布）\nz = ${formatNumber(tVal)}, p = ${formatNumber(pValue)}`
              : `1. 対応の差 dᵢ に符号付順位を付与（|d|=0 は除外）\n2. W⁺, W⁻ から検定統計量、正規近似 z\n3. 両側 p 値\nz = ${formatNumber(tVal)}, p = ${formatNumber(pValue)}`}
          </pre>
          <Table striped bordered size="sm" className="mb-0">
            <tbody>
              <tr>
                <td>{formatStatKeyLabel('method', t)}</td>
                <td>{label}</td>
              </tr>
              <tr>
                <td>{formatStatKeyLabel('z', t)}</td>
                <td>{formatNumber(tVal)}</td>
              </tr>
              <tr>
                <td>{formatStatKeyLabel('p', t)}</td>
                <td>{formatNumber(pValue)}</td>
              </tr>
              <tr>
                <td>
                  {testType === 'paired'
                    ? t('keisuke.dashboard.ttest.beforeColumn') +
                      ' / ' +
                      t('keisuke.dashboard.ttest.afterColumn')
                    : t('keisuke.dashboard.ttest.median')}
                </td>
                <td>
                  {formatNumber(mean1)} / {formatNumber(mean2)} (n = {n1}
                  {testType === 'paired' ? '' : `, ${n2}`})
                </td>
              </tr>
            </tbody>
          </Table>
          <p className="text-muted mt-2 mb-0 small">
            {t('keisuke.dashboard.calculationDetailsNonparametricRef')}
          </p>
        </div>
      );
    }

    const Title = compact ? 'h6' : 'h5';
    const StepTitle = 'h6';

    const pooledStdDev =
      method === 'student' && n1 > 0 && n2 > 0
        ? Math.sqrt(
            ((n1 - 1) * stdDev1 * stdDev1 + (n2 - 1) * stdDev2 * stdDev2) /
              (n1 + n2 - 2)
          )
        : null;

    const studentSeMeanDiff =
      pooledStdDev !== null && n1 > 0 && n2 > 0
        ? pooledStdDev * Math.sqrt(1 / n1 + 1 / n2)
        : null;
    const welchSeMeanDiff =
      testType === 'unpaired' && n1 > 0 && n2 > 0
        ? Math.sqrt((stdDev1 * stdDev1) / n1 + (stdDev2 * stdDev2) / n2)
        : null;

    let pairedSeMeanDiff: number | null = null;
    if (testType === 'paired' && before && after && before.length >= 2) {
      const diffs = after.map((a, i) => a - (before[i] ?? 0));
      const meanD = diffs.reduce((s, v) => s + v, 0) / diffs.length;
      const varD = diffs.reduce((s, v) => s + (v - meanD) ** 2, 0) / (diffs.length - 1);
      pairedSeMeanDiff = Math.sqrt(varD) / Math.sqrt(diffs.length);
    }

    return (
      <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
        <Title className="mb-2">
          {t('keisuke.ttest.calculationProcess')}
        </Title>

        <div className="mb-3">
          <StepTitle className="mt-3">
            {t('keisuke.ttest.step0')}
          </StepTitle>
          <MonoBox>
            {groupNames && groups ? (
              groupNames.map((name) => (
                <div key={name}>
                  {name}: {groups[name]?.map((v) => formatNumber(v)).join(', ')}
                </div>
              ))
            ) : before && after ? (
              <>
                <div>
                  {t('keisuke.dashboard.inputForm.defaultBefore')}:{' '}
                  {before.map(formatNumber).join(', ')}
                </div>
                <div>
                  {t('keisuke.dashboard.inputForm.defaultAfter')}:{' '}
                  {after.map(formatNumber).join(', ')}
                </div>
              </>
            ) : null}
          </MonoBox>
        </div>

        <div className="mb-3">
          <StepTitle className="mt-3">
            {t('keisuke.ttest.step2Mean')}
          </StepTitle>
          <MonoBox>
            {testType === 'paired' && before && after ? (
              (() => {
                const diffs = after.map((a, i) => a - (before[i] ?? 0));
                const sumD = diffs.reduce((s, v) => s + v, 0);
                const nD = diffs.length;
                const meanDiff = nD > 0 ? sumD / nD : NaN;
                return (
                  <div>
                    {t('keisuke.ttest.diffLabel')} (
                    {t('keisuke.dashboard.inputForm.defaultAfter')} −{' '}
                    {t('keisuke.dashboard.inputForm.defaultBefore')}
                    ): (
                    {diffs.map((d) => formatNumber(d)).join(' + ')}) / {nD} = {formatNumber(meanDiff)}
                  </div>
                );
              })()
            ) : testType === 'paired' ? (
              <div>
                {t('keisuke.ttest.diffLabel')} の平均 = {formatNumber(mean2 - mean1)}
              </div>
            ) : (
              groupNames &&
              groups &&
              groupNames.map((groupName, index) => {
                const values = groups[groupName] ?? [];
                const sum = values.reduce((a, b) => a + b, 0);
                const mean = index === 0 ? mean1 : mean2;
                return (
                  <div key={groupName} className="mb-2">
                    {groupName}: ({values.map((v) => formatNumber(v)).join(' + ')}) / {values.length}{' '}
                    = {formatNumber(sum, 4)} / {values.length} = {formatNumber(mean)}
                  </div>
                );
              })
            )}
          </MonoBox>
        </div>

        <div className="mb-3">
          <StepTitle className="mt-3">
            {t('keisuke.ttest.step3StdDev')}
          </StepTitle>
          <MonoBox>
            {testType === 'paired' && before && after && before.length > 0 ? (
              (() => {
                const diffs = after.map((a, i) => a - (before[i] ?? 0));
                const meanD = diffs.reduce((s, v) => s + v, 0) / diffs.length;
                const sumSq = diffs.reduce((s, v) => s + (v - meanD) ** 2, 0);
                const varD = diffs.length > 1 ? sumSq / (diffs.length - 1) : 0;
                const stdD = Math.sqrt(varD);
                return (
                  <div>
                    {t('keisuke.ttest.diffStdDev')}: √
                    {formatNumber(varD, 4)} = {formatNumber(stdD)}
                  </div>
                );
              })()
            ) : testType === 'paired' ? (
              <div>
                {t('keisuke.ttest.diffStdDev')}: {formatNumber(stdDev1)}
              </div>
            ) : (
              groupNames &&
              groupNames.map((groupName, index) => {
                const s = index === 0 ? stdDev1 : stdDev2;
                const n = index === 0 ? n1 : n2;
                return (
                  <div key={groupName} className="mb-2">
                    {groupName}: 不偏分散の平方根 → 標準偏差 = {formatNumber(s)} (n = {n})
                  </div>
                );
              })
            )}
          </MonoBox>
        </div>

        <div className="mb-3">
          <StepTitle className="mt-3">
            {t('keisuke.ttest.step4T')}
          </StepTitle>
          <MonoBox>
            {testType === 'paired' ? (
              <div>
                t = mean(d) / (sd(d) / √n) = {formatNumber(tVal)}
              </div>
            ) : method === 'student' && pooledStdDev !== null ? (
              <div>
                t = (mean1 − mean2) / (sp × √(1/n1 + 1/n2))
                <br />
                t = ({formatNumber(mean1)} − {formatNumber(mean2)}) / ({formatNumber(pooledStdDev)} ×
                √(1/{n1} + 1/{n2})) = {formatNumber(tVal)}
              </div>
            ) : (
              <div>
                t = (mean1 − mean2) / √(s1²/n1 + s2²/n2)
                <br />
                t = ({formatNumber(mean1)} − {formatNumber(mean2)}) / √(
                {formatNumber(stdDev1)}²/{n1} + {formatNumber(stdDev2)}²/{n2}) = {formatNumber(tVal)}
              </div>
            )}
          </MonoBox>
        </div>

        <div className="mb-3">
          <StepTitle className="mt-3">
            {testType === 'paired'
              ? t('keisuke.ttest.step5DfPaired')
              : method === 'student'
                ? t('keisuke.ttest.step5DfStudent')
                : t('keisuke.ttest.step5DfWelch')}
          </StepTitle>
          <MonoBox>
            df = {formatNumber(df, 2)}
          </MonoBox>
        </div>

        <div className="mb-3">
          <StepTitle className="mt-3">
            {t('keisuke.ttest.step6P')}
          </StepTitle>
          <MonoBox>
            |t| = {formatNumber(Math.abs(tVal), 2)}, df = {formatNumber(df, 2)} → p = {formatNumber(pValue)}
          </MonoBox>
        </div>

        {ci && (
          <div className="mb-3">
            <StepTitle className="mt-3">
              {t('keisuke.ttest.step7CI')}
            </StepTitle>
            <MonoBox>
              {testType === 'paired' && pairedSeMeanDiff !== null ? (
                <div className="mb-2">
                  {t('keisuke.ttest.ciFormulaPaired')}
                  <br />
                  {t('keisuke.ttest.ciSeLabel')} ={' '}
                  {formatNumber(pairedSeMeanDiff)}
                </div>
              ) : testType === 'unpaired' && method === 'student' && studentSeMeanDiff !== null ? (
                <div className="mb-2">
                  {t('keisuke.ttest.ciFormulaStudent')}
                  <br />
                  {t('keisuke.ttest.ciSeLabel')} = s_p × √(1/n1 + 1/n2) ={' '}
                  {formatNumber(studentSeMeanDiff)}
                </div>
              ) : testType === 'unpaired' && welchSeMeanDiff !== null ? (
                <div className="mb-2">
                  {t('keisuke.ttest.ciFormulaWelch')}
                  <br />
                  {t('keisuke.ttest.ciSeLabel')} = √(s1²/n1 + s2²/n2) ={' '}
                  {formatNumber(welchSeMeanDiff)}
                </div>
              ) : null}
              <div>
                [{formatNumber(ci.lower)}, {formatNumber(ci.upper)}]
              </div>
              <div className="text-muted mt-2">
                {t('keisuke.ttest.ciNoteTcritical')}
              </div>
            </MonoBox>
          </div>
        )}

        <Table striped bordered size="sm" className="mb-0">
          <tbody>
            <tr><td>{formatStatKeyLabel('t', t)}</td><td>{formatNumber(tVal)}</td></tr>
            <tr><td>{formatStatKeyLabel('df', t)}</td><td>{df}</td></tr>
            <tr><td>{formatStatKeyLabel('p', t)}</td><td>{formatNumber(pValue)}</td></tr>
            <tr>
              <td>{formatStatKeyPairLabel('mean1', 'mean2', t, namedGroups)}</td>
              <td>{formatNumber(mean1)} / {formatNumber(mean2)}</td>
            </tr>
            <tr>
              <td>{formatStatKeyPairLabel('n1', 'n2', t, namedGroups)}</td>
              <td>{n1} / {n2}</td>
            </tr>
            <tr>
              <td>{formatStatKeyLabel('method', t)}</td>
              <td>{formatMethodStatValue(method, t)}</td>
            </tr>
            {ttestComparisonCount > 1 ? (
              <>
                <tr>
                  <td>{t('keisuke.ttest.bonferroniPValue')}</td>
                  <td>
                    {formatNumber(Math.min(1, pValue * ttestComparisonCount))}
                  </td>
                </tr>
                <tr>
                  <td>{t('keisuke.ttest.bonferroniPValueOneSided')}</td>
                  <td>
                    {formatNumber(
                      Math.min(
                        1,
                        Number(result.pValueOneSided ?? pValue / 2) * ttestComparisonCount
                      )
                    )}
                  </td>
                </tr>
              </>
            ) : null}
          </tbody>
        </Table>
      </div>
    );
  }

  if (analysisType === 'anova') {
    const result = r as Record<string, unknown>;

    const kw = result.kruskalWallis as
      | { H: number; pValue: number; df: number; groups: Array<{ name: string; n: number; median: number }> }
      | undefined;
    if (kw) {
      return (
        <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
          <h6 className="mb-2">
            {t('keisuke.dashboard.anova.kruskalWallis')}
          </h6>
          <pre className="bg-light p-3 rounded mb-3 small">
            {`H = (12 / (N(N+1))) × Σ(Rᵢ²/nᵢ) − 3(N+1)\nH = ${formatNumber(kw.H)}, df = ${kw.df}, p = ${formatNumber(kw.pValue)}`}
          </pre>
          <Table striped bordered size="sm" className="mb-0">
            <tbody>
              {kw.groups.map((g) => (
                <tr key={g.name}>
                  <td>{g.name}</td>
                  <td>
                    n = {g.n}, {t('keisuke.dashboard.ttest.median')} ={' '}
                    {formatNumber(g.median)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }

    if (
      result.groups &&
      Array.isArray(result.groups) &&
      !result.factorA &&
      analysisOptions &&
      gridData.length > 0
    ) {
      return (
        <AnovaCalculationDetails
          rawResult={rawResult}
          analysisOptions={analysisOptions}
          gridData={gridData}
          anovaPairwise={anovaPairwise}
          compact={compact}
        />
      );
    }

    if (result.groups && Array.isArray(result.groups) && !result.factorA) {
      const groups = result.groups as Array<{ name: string; mean: number; n: number }>;
      return (
        <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
          <h6 className="mb-2">{t('keisuke.anova.oneWay.flowTitle')}</h6>
          <Table striped bordered size="sm" className="mb-3">
            <tbody>
              <tr>
                <td>{t('keisuke.anova.f')}</td>
                <td>{formatNumber(Number(result.f))}</td>
              </tr>
              <tr>
                <td>
                  {t('keisuke.anova.dfBetween')} / {t('keisuke.anova.dfWithin')}
                </td>
                <td>
                  {String(result.dfBetween ?? '')} / {String(result.dfWithin ?? '')}
                </td>
              </tr>
              <tr>
                <td>{formatStatKeyLabel('p', t)}</td>
                <td>{formatNumber(Number(result.pValue))}</td>
              </tr>
              <tr>
                <td>{t('keisuke.anova.ssBetween')}</td>
                <td>{formatNumber(Number(result.ssBetween))}</td>
              </tr>
              <tr>
                <td>{t('keisuke.anova.ssWithin')}</td>
                <td>{formatNumber(Number(result.ssWithin))}</td>
              </tr>
              {groups.map((g) => (
                <tr key={g.name}>
                  <td>
                    {g.name} ({t('keisuke.anova.mean')}, {t('keisuke.anova.n')})
                  </td>
                  <td>
                    {formatNumber(g.mean)}, {g.n}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <pre className="bg-light p-3 rounded mb-0 small">
            {`一元配置 ANOVA: SS_between = ${formatNumber(Number(result.ssBetween))}, SS_within = ${formatNumber(Number(result.ssWithin))}, F = ${formatNumber(Number(result.f))}, p = ${formatNumber(Number(result.pValue))}`}
          </pre>
          {result.varianceComponents ? (
            <p className="mt-2 mb-0 small text-muted">
              LMM: σ²(被験体) ={' '}
              {formatNumber(Number((result.varianceComponents as { randomIntercept: number }).randomIntercept))},
              σ²(残差) ={' '}
              {formatNumber(Number((result.varianceComponents as { residual: number }).residual))}
            </p>
          ) : null}
        </div>
      );
    }
    if (result.factorA && result.factorB && result.interaction) {
      const fa = result.factorA as Record<string, unknown>;
      const fb = result.factorB as Record<string, unknown>;
      const fi = result.interaction as Record<string, unknown>;
      const design = String(result.design ?? 'independent');
      const designLabel =
        design === 'lmm'
          ? t('keisuke.anova.methodLabels.oneWay.lmm')
          : design === 'mixed'
            ? t('keisuke.dashboard.anova.twoWayMixed')
            : design === 'full-repeated'
              ? t('keisuke.anova.methodLabels.twoWay.fullRepeated')
              : t('keisuke.dashboard.anova.twoWayIndependent');
      return (
        <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
          <h6 className="mb-2">
            {t('keisuke.anova.twoWay.detailTitle')} ({designLabel})
          </h6>
          {design === 'full-repeated' ? (
            <p className="small text-muted">
              {t('keisuke.anova.twoWay.fullRepeatedPostHocNote')}
            </p>
          ) : null}
          <Table striped bordered size="sm">
            <tbody>
              <tr><td>{t('keisuke.anova.twoWay.factorAFp')}</td><td>{formatNumber(Number(fa.f))}, {formatNumber(Number(fa.pValue))}</td></tr>
              <tr><td>{t('keisuke.anova.twoWay.factorBFp')}</td><td>{formatNumber(Number(fb.f))}, {formatNumber(Number(fb.pValue))}</td></tr>
              <tr><td>{t('keisuke.anova.twoWay.interactionFp')}</td><td>{formatNumber(Number(fi.f))}, {formatNumber(Number(fi.pValue))}</td></tr>
            </tbody>
          </Table>
        </div>
      );
    }
  }

  if (analysisType === 'dose-response') {
    return <DoseResponseCalculationDetails rawResult={rawResult} compact={compact} />;
  }

  if (analysisType === 'basic-stats' && r.n != null) {
    const bs = r as Record<string, unknown>;
    const ci = bs.confidenceInterval as { lower: number; upper: number } | undefined;
    return (
      <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
        <h6 className="mb-2">{t('keisuke.basicStats.detailTitle')}</h6>
        <Table striped bordered size="sm" className="mb-0">
          <tbody>
            <tr>
              <td>{t('keisuke.basicStatistics.n')}</td>
              <td>{String(bs.n)}</td>
            </tr>
            <tr>
              <td>{t('keisuke.basicStatistics.mean')}</td>
              <td>{formatNumber(Number(bs.mean))}</td>
            </tr>
            <tr>
              <td>{t('keisuke.basicStatistics.median')}</td>
              <td>{formatNumber(Number(bs.median))}</td>
            </tr>
            <tr>
              <td>{t('keisuke.basicStatistics.stdDev')}</td>
              <td>{formatNumber(Number(bs.stdDev))}</td>
            </tr>
            <tr>
              <td>{t('keisuke.basicStatistics.stdError')}</td>
              <td>{formatNumber(Number(bs.stdError))}</td>
            </tr>
            {ci ? (
              <tr>
                <td>{t('keisuke.dashboard.resultStats.ci95', { defaultValue: '95% CI' })}</td>
                <td>
                  [{formatNumber(Number(ci.lower))}, {formatNumber(Number(ci.upper))}]
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </div>
    );
  }

  if (analysisType === 'qpcr' && Array.isArray(r.samples)) {
    const samples = r.samples as Array<{ sampleName: string; relativeExpression: number }>;
    return (
      <div className={`small ${compact ? 'calculation-details-content--compact' : ''}`}>
        <h6 className="mb-2">qPCR</h6>
        <Table striped bordered size="sm" className="mb-0">
          <tbody>
            {samples.map((s) => (
              <tr key={s.sampleName}>
                <td>{s.sampleName}</td>
                <td>{formatNumber(s.relativeExpression)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <p className="text-muted mb-0">
      {t('keisuke.dashboard.calculationDetailsNotAvailable')}
    </p>
  );
}
